import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, Subscription, retry, throwError, timer } from 'rxjs';
import {
  Stripe,
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeElementChangeEvent,
} from '@stripe/stripe-js';
import { ConfirmActionModalComponent } from '../confirm-action-modal/confirm-action-modal.component';
import { ConnectedAccountStripeService } from '../../../services/connected-account-stripe.service';
import { LoggerService } from '../../../services/logger.service';
import {
  CardSetup,
  CardSetupResult,
  PaymentMethodErrorCode,
  SubscriptionPaymentMethodService,
} from '../../../services/subscription-payment-method.service';
import { ApiResponse } from '../../types';
import { paymentMethodErrorCopy } from '../../utils/payment-method.copy';
import { createStripeElementConfig } from '../../utils/stripe-elements.util';

type CardField = 'cardNumber' | 'cardExpiry' | 'cardCvc';

/** What Add New Card hands back: the card to select, and whether it was already on file. */
export interface AddedCard {
  paymentMethodId: string;
  /** The card typed was already on file: svc-order removed the copy and this is the original. */
  duplicate: boolean;
  /**
   * svc-order could not finish the setup, so whether the card was already on file is unknown
   * and a copy of it may be left in Stripe.
   */
  unfinished?: boolean;
}

/** Finishing the setup is tried again after a blip (svc-order restarting, a timeout, Stripe). */
export const FINISH_RETRIES = 3;
export const FINISH_RETRY_DELAY_MS = 1000;
const RETRIED_FINISH_CODES: ReadonlySet<PaymentMethodErrorCode> = new Set<PaymentMethodErrorCode>([
  'UNKNOWN',
  'stripe_unavailable',
  // Another change to the membership's card holds its lock for a moment.
  'card_change_in_progress',
]);

const SETUP_FAILED = 'Could not start adding a card. Try again.';
const SAVE_FAILED = 'The card could not be saved. Check the details and try again.';

/**
 * CWS-1536 "Add New Card": saves a card to the membership's Stripe customer.
 *
 * The number, expiry and CVV are Stripe's own fields, on the membership location's connected
 * account; svc-order only creates the SetupIntent, so no card data passes through our servers.
 * Then svc-order finishes the setup: a card already on file comes back as the original, with the
 * copy removed. The Payment method pop-up offers the card as the renewal card.
 *
 * With `imported`, the card is saved for an imported membership its vendor keeps billing, on the
 * Stripe account of the shop its gate order is at, and is never charged here.
 *
 * @selector app-add-card-modal
 * @input subscriptionId - The membership the card is for.
 * @input imported - An imported membership: save the card only (svc-order imported-cards).
 * @output saved - The card was saved, or was already on file; see AddedCard.
 * @output back - Back to the saved cards.
 * @output cancel - Close without saving.
 */
@Component({
  selector: 'app-add-card-modal',
  standalone: true,
  imports: [CommonModule, ConfirmActionModalComponent],
  templateUrl: './add-card-modal.component.html',
  styleUrls: ['./add-card-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AddCardModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) subscriptionId = '';
  @Input() imported = false;

  @Output() saved = new EventEmitter<AddedCard>();
  @Output() back = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChild('cardNumberHost', { static: true }) cardNumberHost!: ElementRef<HTMLElement>;
  @ViewChild('cardExpiryHost', { static: true }) cardExpiryHost!: ElementRef<HTMLElement>;
  @ViewChild('cardCvcHost', { static: true }) cardCvcHost!: ElementRef<HTMLElement>;

  /** The SetupIntent and Stripe's fields are still being set up. */
  preparing = true;
  saving = false;
  /** The card is saved; Save card stays off while this step is still on screen. */
  cardSaved = false;
  /** Setup or save failed; replaces the field errors while shown. */
  failure = '';

  private readonly complete: Record<CardField, boolean> = {
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  };
  private readonly fieldErrors: Record<CardField, string> = {
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
  };
  private stripe: Stripe | null = null;
  private clientSecret = '';
  private cardNumber: StripeCardNumberElement | null = null;
  private fields: (StripeCardNumberElement | StripeCardExpiryElement | StripeCardCvcElement)[] = [];
  private setupRequest: Subscription | null = null;
  private completeRequest: Subscription | null = null;
  private destroyed = false;

  constructor(
    private readonly paymentMethods: SubscriptionPaymentMethodService,
    private readonly stripeLoader: ConnectedAccountStripeService,
    private readonly zone: NgZone,
    private readonly logger: LoggerService
  ) {}

  get canSave(): boolean {
    return (
      !this.preparing &&
      !this.saving &&
      !this.cardSaved &&
      !this.failure &&
      this.complete.cardNumber &&
      this.complete.cardExpiry &&
      this.complete.cardCvc
    );
  }

  /** The first problem Stripe reports with what was typed, or the setup/save failure. */
  get errorMessage(): string {
    return (
      this.failure ||
      this.fieldErrors.cardNumber ||
      this.fieldErrors.cardExpiry ||
      this.fieldErrors.cardCvc
    );
  }

  /** The field hosts are static queries, so Stripe can mount into them from here. */
  ngOnInit(): void {
    this.setupRequest = this.startSetup().subscribe({
      next: response => void this.mountFields(response?.data),
      error: (code: PaymentMethodErrorCode) => this.fail(this.setupFailure(code)),
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.setupRequest?.unsubscribe();
    this.completeRequest?.unsubscribe();
    this.fields.forEach(field => field.destroy());
    this.fields = [];
  }

  async save(): Promise<void> {
    if (!this.canSave || !this.stripe || !this.cardNumber) {
      return;
    }
    this.saving = true;
    let result: Awaited<ReturnType<Stripe['confirmCardSetup']>>;
    try {
      result = await this.stripe.confirmCardSetup(this.clientSecret, {
        payment_method: { card: this.cardNumber },
      });
    } catch {
      // Stripe.js throws rather than answering when it cannot run the confirm (a field gone from
      // the page, the 3D Secure frame failing to load): without this, Saving... never ends.
      if (!this.destroyed) {
        this.saving = false;
        this.failure = SAVE_FAILED;
      }
      return;
    }
    if (this.destroyed) {
      return;
    }
    if (result.error) {
      this.saving = false;
      // Stripe's own words for a declined or mistyped card are the useful ones here.
      this.failure = result.error.message || SAVE_FAILED;
      return;
    }
    const paymentMethod = result.setupIntent?.payment_method;
    const paymentMethodId = typeof paymentMethod === 'string' ? paymentMethod : paymentMethod?.id;
    if (!paymentMethodId) {
      this.saving = false;
      this.failure = SAVE_FAILED;
      return;
    }
    // The SetupIntent has succeeded; confirming it again would only fail.
    this.cardSaved = true;
    this.finishSetup(result.setupIntent?.id, paymentMethodId);
  }

  /**
   * svc-order finishes the setup, which spots a card already on file and removes the copy. Stays
   * "Saving..." until it answers, trying again through a blip. The card is saved either way, so
   * a failure still hands back the card Stripe saved, marked unfinished, and is logged with the
   * ids that find a leftover copy.
   */
  private finishSetup(setupIntentId: string | undefined, paymentMethodId: string): void {
    const done = (added: AddedCard): void => {
      this.saving = false;
      this.saved.emit(added);
    };
    if (!setupIntentId) {
      done({ paymentMethodId, duplicate: false });
      return;
    }
    this.completeRequest = this.finishRequest(setupIntentId)
      .pipe(
        retry({
          count: FINISH_RETRIES,
          delay: (code: PaymentMethodErrorCode, attempt: number) =>
            RETRIED_FINISH_CODES.has(code)
              ? timer(attempt * FINISH_RETRY_DELAY_MS)
              : throwError(() => code),
        })
      )
      .subscribe({
        next: response =>
          done({
            paymentMethodId: response?.data?.payment_method_id || paymentMethodId,
            duplicate: response?.data?.duplicate === true,
          }),
        error: (code: PaymentMethodErrorCode) => {
          this.logger.warn(
            'subscription_payment_method.setup_not_finished',
            { subscriptionId: this.subscriptionId, setupIntentId, paymentMethodId, code },
            'AddCardModal'
          );
          done({ paymentMethodId, duplicate: false, unfinished: true });
        },
      });
  }

  private startSetup(): Observable<ApiResponse<CardSetup>> {
    return this.imported
      ? this.paymentMethods.createImportedCardSetup(this.subscriptionId)
      : this.paymentMethods.createCardSetup(this.subscriptionId);
  }

  private finishRequest(setupIntentId: string): Observable<ApiResponse<CardSetupResult>> {
    return this.imported
      ? this.paymentMethods.completeImportedCardSetup(this.subscriptionId, setupIntentId)
      : this.paymentMethods.completeCardSetup(this.subscriptionId, setupIntentId);
  }

  private async mountFields(setup: CardSetup | undefined): Promise<void> {
    if (!setup?.client_secret || !setup.stripe_account_id) {
      this.fail(SETUP_FAILED);
      return;
    }
    let stripe: Stripe | null = null;
    try {
      stripe = await this.stripeLoader.load(setup.stripe_account_id);
    } catch {
      stripe = null;
    }
    if (this.destroyed) {
      return;
    }
    if (!stripe) {
      this.fail(SETUP_FAILED);
      return;
    }
    this.stripe = stripe;
    this.clientSecret = setup.client_secret;

    const elements = stripe.elements();
    const cardNumber = elements.create('cardNumber', {
      ...createStripeElementConfig('1234 1234 1234 1234'),
      showIcon: true,
      // Staff type the customer's card on a shop computer: Link's Autofill would offer whatever
      // Link wallet that browser remembers (a staff member's, an earlier customer's) for this
      // membership's renewals.
      disableLink: true,
    });
    const cardExpiry = elements.create('cardExpiry', createStripeElementConfig('MM/YY'));
    const cardCvc = elements.create('cardCvc', createStripeElementConfig('123'));
    cardNumber.on('change', this.onFieldChange('cardNumber'));
    cardExpiry.on('change', this.onFieldChange('cardExpiry'));
    cardCvc.on('change', this.onFieldChange('cardCvc'));
    cardNumber.mount(this.cardNumberHost.nativeElement);
    cardExpiry.mount(this.cardExpiryHost.nativeElement);
    cardCvc.mount(this.cardCvcHost.nativeElement);

    this.cardNumber = cardNumber;
    this.fields = [cardNumber, cardExpiry, cardCvc];
    this.preparing = false;
  }

  private onFieldChange(field: CardField): (event: StripeElementChangeEvent) => void {
    // Stripe's callbacks may run outside Angular's zone; run them inside it so the view updates.
    return event =>
      this.zone.run(() => {
        this.complete[field] = event.complete;
        this.fieldErrors[field] = event.error?.message ?? '';
        this.failure = '';
      });
  }

  private setupFailure(code: PaymentMethodErrorCode): string {
    return code === 'UNKNOWN' ? SETUP_FAILED : paymentMethodErrorCopy(code);
  }

  private fail(message: string): void {
    this.preparing = false;
    this.failure = message;
  }
}
